import type { Todo } from "../../domain/entities.ts";
import { resolveWallTime, shiftWallTime, wallTimeInZone } from "./task-time.ts";

export function nextRecurringDate(todo: Todo): string {
  const recurrence = todo.recurrence!;
  const scheduledBase = recurrenceBaseDate(todo);
  const base = recurrence.basis === "completion" && todo.completedAt ? dateInTimezone(new Date(todo.completedAt), recurrence.timezone) : scheduledBase;
  let next: string;
  if (recurrence.frequency === "yearly") next = recurrence.basis === "completion" ? addYears(base, recurrence.interval) : addYears(todo.recurrenceAnchorDate || base, recurrence.interval * ((todo.recurrenceOccurrence || 0) + 1));
  else if (recurrence.frequency === "monthly" && recurrence.monthlyMode === "last_working_day") next = lastWorkingDay(addMonths(`${base.slice(0, 8)}01`, recurrence.interval));
  else if (recurrence.frequency === "monthly" && recurrence.monthlyMode === "ordinal") next = ordinalWeekday(addMonths(`${base.slice(0, 8)}01`, recurrence.interval), recurrence.ordinal || 1, recurrence.weekday ?? new Date(`${base}T00:00:00Z`).getUTCDay());
  else if (recurrence.frequency === "monthly") next = recurrence.basis === "completion" ? addMonths(base, recurrence.interval) : addMonths(todo.recurrenceAnchorDate || base, recurrence.interval * ((todo.recurrenceOccurrence || 0) + 1));
  else if (recurrence.frequency === "weekly" && recurrence.weekdays?.length) next = nextSelectedWeekday(base, recurrence.weekdays, recurrence.interval, todo.recurrenceAnchorDate || base);
  else if (recurrence.frequency === "weekdays") {
    next = base;
    let remaining = recurrence.interval;
    while (remaining > 0) {
      next = addDays(next, 1);
      const day = new Date(`${next}T00:00:00Z`).getUTCDay();
      if (day !== 0 && day !== 6) remaining--;
    }
  } else next = addDays(base, recurrence.frequency === "weekly" ? recurrence.interval * 7 : recurrence.interval);
  while ((recurrence.skippedDates || []).includes(next)) next = nextDateFrom(next, recurrence, todo.recurrenceAnchorDate || base);
  return next;
}

function recurrenceBaseDate(todo: Todo): string {
  const timezone = todo.recurrence!.timezone;
  return todo.dueDate || todo.startDate || (todo.startAt ? dateInTimezone(new Date(todo.startAt), timezone) : dateInTimezone(new Date(), timezone));
}

function nextDateFrom(base: string, recurrence: NonNullable<Todo["recurrence"]>, anchor: string): string {
  const task = { dueDate: base, startDate: base, recurrence: { ...recurrence, skippedDates: [] }, recurrenceAnchorDate: recurrence.frequency === "monthly" || recurrence.frequency === "yearly" ? base : anchor, recurrenceOccurrence: 0 } as unknown as Todo;
  return nextRecurringDate(task);
}

export function recurrenceHasEnded(todo: Todo, nextDate: string, nextOccurrence: number): boolean {
  const rule = todo.recurrence;
  return !!rule && ((!!rule.endDate && nextDate > rule.endDate) || (!!rule.occurrenceCount && nextOccurrence >= rule.occurrenceCount));
}

export function projectedRecurringDates(todo: Todo, count = 6): string[] {
  if (!todo.recurrence) return [];
  const dates: string[] = [];
  let cursor = structuredClone(todo);
  for (let index = 0; index < count; index++) {
    const occurrence = (cursor.recurrenceOccurrence || 0) + 1;
    const date = nextRecurringDate(cursor);
    if (recurrenceHasEnded(cursor, date, occurrence)) break;
    dates.push(date);
    cursor = { ...cursor, dueDate: date, startDate: date, recurrenceAnchorDate: cursor.recurrenceAnchorDate || recurrenceBaseDate(todo), recurrenceOccurrence: occurrence };
  }
  return dates;
}

export function projectedRecurringDatesInRange(todo: Todo, start: string, end: string): string[] {
  if (!todo.recurrence || start > end) return [];
  const dates: string[] = [];
  const anchor = recurrenceBaseDate(todo);
  let cursor = structuredClone(todo);
  const rule = todo.recurrence;
  if (rule.basis !== "completion" && !rule.skippedDates?.length && (rule.frequency === "daily" || rule.frequency === "weekly" && !rule.weekdays?.length)) {
    const step = Math.max(1, rule.interval) * (rule.frequency === "weekly" ? 7 : 1);
    const skipped = Math.max(0, Math.ceil(differenceInDays(anchor, start) / step) - 1);
    if (skipped) cursor = { ...cursor, dueDate: addDays(anchor, skipped * step), startDate: addDays(anchor, skipped * step), recurrenceOccurrence: (cursor.recurrenceOccurrence || 0) + skipped };
  }
  for (let index = 0; index < 100000; index++) {
    const occurrence = (cursor.recurrenceOccurrence || 0) + 1;
    const previousDate = recurrenceBaseDate(cursor);
    const date = nextRecurringDate(cursor);
    if (date <= previousDate || date > end || recurrenceHasEnded(cursor, date, occurrence)) break;
    if (date >= start) dates.push(date);
    cursor = { ...cursor, dueDate: date, startDate: date, recurrenceAnchorDate: cursor.recurrenceAnchorDate || anchor, recurrenceOccurrence: occurrence };
  }
  return dates;
}

export function recurrenceDescription(recurrence: NonNullable<Todo["recurrence"]>): string {
  const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  let description: string;
  if (recurrence.frequency === "weekdays") description = recurrence.interval === 1 ? "Every weekday" : `Every ${recurrence.interval} weekdays`;
  else if (recurrence.frequency === "weekly" && recurrence.weekdays?.length) {
    const days = [...new Set(recurrence.weekdays)].sort().map(day => dayNames[day]).filter(Boolean).join(", ");
    description = `${recurrence.interval === 1 ? "Every week" : `Every ${recurrence.interval} weeks`} on ${days}`;
  } else if (recurrence.frequency === "monthly" && recurrence.monthlyMode === "last_working_day") description = `${recurrence.interval === 1 ? "Every month" : `Every ${recurrence.interval} months`} on the last working day`;
  else if (recurrence.frequency === "monthly" && recurrence.monthlyMode === "ordinal") {
    const ordinal = recurrence.ordinal === -1 ? "last" : ["", "first", "second", "third", "fourth", "fifth"][recurrence.ordinal || 1];
    description = `${recurrence.interval === 1 ? "Every month" : `Every ${recurrence.interval} months`} on the ${ordinal} ${dayNames[recurrence.weekday ?? 1]}`;
  } else {
    const units = { daily: "day", weekly: "week", monthly: "month", yearly: "year" } as const;
    const unit = units[recurrence.frequency as keyof typeof units] || "day";
    description = recurrence.interval === 1 ? `Every ${unit}` : `Every ${recurrence.interval} ${unit}s`;
  }
  if (recurrence.basis === "completion") description += ", from completion";
  if (recurrence.endDate) description += `, until ${recurrence.endDate}`;
  else if (recurrence.occurrenceCount) description += `, ${recurrence.occurrenceCount} times`;
  return description;
}

export function nextRecurringReminder(todo: Todo, dueDate: string): string | null {
  if (!todo.reminderAt || !todo.recurrence) return null;
  const base = todo.dueDate || todo.startDate || todo.wallStartAt?.slice(0, 10) || (todo.startAt ? dateInTimezone(new Date(todo.startAt), todo.recurrence.timezone) : dueDate);
  const shift = differenceInDays(base, dueDate);
  if (todo.scheduleMode === "floating" && todo.scheduleTimeZone) return resolveWallTime(shiftWallTime(wallTimeInZone(todo.reminderAt, todo.scheduleTimeZone), shift)!, todo.scheduleTimeZone);
  return new Date(new Date(todo.reminderAt).getTime() + shift * 86_400_000).toISOString();
}

export function addDays(value: string, days: number): string {
  const date = new Date(`${value}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

export function addMonths(value: string, months: number): string {
  const [year, month, day] = value.split("-").map(Number);
  const first = new Date(Date.UTC(year, month - 1 + months, 1));
  const lastDay = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0)).getUTCDate();
  return `${first.getUTCFullYear()}-${String(first.getUTCMonth() + 1).padStart(2, "0")}-${String(Math.min(day, lastDay)).padStart(2, "0")}`;
}

export function addYears(value: string, years: number): string { return addMonths(value, years * 12); }

export function ordinalWeekday(monthDate: string, ordinal: number, weekday: number): string {
  const [year, month] = monthDate.split("-").map(Number);
  if (ordinal === -1) {
    const last = new Date(Date.UTC(year, month, 0));
    last.setUTCDate(last.getUTCDate() - ((last.getUTCDay() - weekday + 7) % 7));
    return last.toISOString().slice(0, 10);
  }
  const first = new Date(Date.UTC(year, month - 1, 1));
  const day = 1 + ((weekday - first.getUTCDay() + 7) % 7) + (Math.max(1, ordinal) - 1) * 7;
  const candidate = new Date(Date.UTC(year, month - 1, day));
  return candidate.getUTCMonth() === month - 1 ? candidate.toISOString().slice(0, 10) : ordinalWeekday(monthDate, -1, weekday);
}

export function lastWorkingDay(monthDate: string): string {
  const [year, month] = monthDate.split("-").map(Number);
  const date = new Date(Date.UTC(year, month, 0));
  while (date.getUTCDay() === 0 || date.getUTCDay() === 6) date.setUTCDate(date.getUTCDate() - 1);
  return date.toISOString().slice(0, 10);
}

function nextSelectedWeekday(base: string, weekdays: number[], interval: number, anchor: string): string {
  const selected = new Set(weekdays);
  let candidate = base;
  for (let attempts = 0; attempts < 3700; attempts++) {
    candidate = addDays(candidate, 1);
    const weeks = Math.floor(Math.max(0, differenceInDays(anchor, candidate)) / 7);
    if (selected.has(new Date(`${candidate}T00:00:00Z`).getUTCDay()) && weeks % interval === 0) return candidate;
  }
  return addDays(base, 7 * interval);
}

export async function deterministicOccurrenceId(seriesId: string, occurrence: number): Promise<string> {
  const hash = new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(`${seriesId}:${occurrence}`))).slice(0, 16);
  hash[6] = (hash[6] & 0x0f) | 0x50;
  hash[8] = (hash[8] & 0x3f) | 0x80;
  const hex = [...hash].map(value => value.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function differenceInDays(from: string, to: string): number {
  return Math.round((new Date(`${to}T00:00:00Z`).getTime() - new Date(`${from}T00:00:00Z`).getTime()) / 86_400_000);
}

function dateTimeParts(date: Date, timezone: string): { year: number; month: number; day: number; hour: number; minute: number } {
  const parts = new Intl.DateTimeFormat("en-US", { timeZone: timezone, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(date);
  const value = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find(part => part.type === type)?.value);
  return { year: value("year"), month: value("month"), day: value("day"), hour: value("hour"), minute: value("minute") };
}

export function dateInTimezone(date: Date, timezone: string): string {
  const parts = dateTimeParts(date, timezone);
  return `${parts.year}-${String(parts.month).padStart(2, "0")}-${String(parts.day).padStart(2, "0")}`;
}
